/**
 * Land und Gewässer aus Koordinaten — offline, aus einer Tabelle.
 *
 * Ein Tauchgang von 2004 hat einen Platznamen ("Barracuda Point") und sonst
 * nichts: kein Land, kein Gewässer. Sobald aber Koordinaten dranhängen — vom
 * Garmin-GPS oder von Hand über den Reiter „Verorten" — lässt sich beides
 * ableiten, ohne irgendwen zu fragen.
 *
 * Abgeleitet, nicht abgefragt: Das hier ist eine Konstante wie die Landesmitten
 * in `verorten.js`, kein Geocoding. CLAUDE.md schliesst den Abruf bei einem
 * Dienst zur Laufzeit aus — eine Tabelle im Programm ist davon nicht betroffen,
 * funktioniert offline und liefert bei derselben Koordinate immer dasselbe.
 *
 * **Was hier steht, wird nie gespeichert.** Der abgeleitete Wert erscheint auf
 * der Detailseite und ist dort als abgeleitet markiert; `sites.country` und
 * `sites.water_name` bleiben unangetastet. Eine Schätzung, die sich als Tatsache
 * in die Stammdaten schreibt, käme über den Abzug in die Datenbank und wäre
 * später nicht mehr von gepflegten Werten zu unterscheiden.
 *
 * Genauigkeit: Kästen, keine Grenzverläufe. Für ein Tauchlogbuch reicht das —
 * gefragt ist „Philippinen oder Indonesien", nicht der Grenzstein. Wo zwei
 * Kästen übereinanderliegen (Borneo: Malaysia und Indonesien), gewinnt der
 * kleinere; die Länder sind dafür in mehrere enge Kästen zerlegt statt in einen
 * grossen. Passt nichts, kommt `null` zurück und das Feld bleibt leer —
 * lieber nichts als etwas Falsches.
 *
 * Kasten = [südLat, nordLat, westLon, ostLon]. Ist westLon > ostLon, läuft der
 * Kasten über den 180. Längengrad (Fiji).
 */

/** Länge in Längengraden — über den 180. Grad hinweg richtig gerechnet. */
function spanne(west, ost) {
  return ost >= west ? ost - west : 360 - west + ost;
}

function flaeche(kasten) {
  return (kasten[1] - kasten[0]) * spanne(kasten[2], kasten[3]);
}

function trifft(kasten, lat, lon) {
  if (lat < kasten[0] || lat > kasten[1]) return false;
  return kasten[2] <= kasten[3]
    ? lon >= kasten[2] && lon <= kasten[3]
    : lon >= kasten[2] || lon <= kasten[3];   // über den 180. Grad
}

/**
 * Der kleinste Kasten, der die Koordinate enthält, gewinnt.
 *
 * Das ist die ganze Auflösungsregel: Der Golf von Aqaba liegt im Roten Meer,
 * das Rote Meer im Indischen Ozean. Wer den kleinsten nimmt, bekommt die
 * genaueste Antwort, ohne dass die Tabelle eine Rangfolge mitführen muss.
 */
export function kleinsterTreffer(bereiche, lat, lon) {
  let treffer = null;
  let kleinste = Infinity;
  for (const bereich of bereiche) {
    for (const kasten of bereich.kaesten) {
      if (!trifft(kasten, lat, lon)) continue;
      const gross = flaeche(kasten);
      if (gross < kleinste) {
        kleinste = gross;
        treffer = bereich;
      }
    }
  }
  return treffer;
}

function brauchbar(lat, lon) {
  return typeof lat === 'number' && typeof lon === 'number'
    && Number.isFinite(lat) && Number.isFinite(lon)
    && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    && !(lat === 0 && lon === 0);   // Nullmeridian im Golf von Guinea, kein Tauchplatz
}

// --------------------------------------------------------------------- Länder
//
// Enthalten sind die Tauchnationen des Bestands (dieselben wie in der
// Ländertabelle der Pipeline) und ihre Nachbarn — genug, damit ein Platz nicht
// dem falschen Staat zufällt. Die Kästen greifen etwa ein halbes Grad aufs
// Wasser hinaus: Tauchplätze liegen vor der Küste, nicht darauf.

export const LAENDER = [
  // --- Rotes Meer. Am Golf von Aqaba liegen vier Länder auf zwanzig Kilometern;
  // die Küstenkästen dort sind bewusst schmal, sonst bekäme Aqaba Eilats Fahne.
  { code: 'EG', name: 'Ägypten', kaesten: [[21.9, 31.7, 24.6, 34.9]] },
  { code: 'IL', name: 'Israel', kaesten: [[29.44, 33.35, 34.24, 35.68], [29.40, 29.60, 34.86, 34.99]] },
  { code: 'JO', name: 'Jordanien', kaesten: [[29.15, 33.40, 34.95, 39.35], [29.30, 29.60, 34.99, 35.12]] },
  { code: 'SA', name: 'Saudi-Arabien', kaesten: [[15.9, 32.2, 34.4, 55.7]] },
  { code: 'SD', name: 'Sudan', kaesten: [[8.5, 22.3, 21.6, 39.2]] },
  { code: 'ER', name: 'Eritrea', kaesten: [[12.3, 18.1, 36.3, 43.4]] },
  { code: 'DJ', name: 'Dschibuti', kaesten: [[10.8, 12.8, 41.6, 43.6]] },
  { code: 'YE', name: 'Jemen', kaesten: [[11.8, 19.1, 41.8, 54.6]] },
  { code: 'OM', name: 'Oman', kaesten: [[16.4, 26.6, 51.8, 60.0]] },
  { code: 'AE', name: 'Vereinigte Arabische Emirate', kaesten: [[22.4, 26.2, 51.4, 56.5]] },

  // --- Indischer Ozean
  { code: 'MV', name: 'Malediven', kaesten: [[-1.1, 7.3, 72.3, 73.9]] },
  { code: 'LK', name: 'Sri Lanka', kaesten: [[5.6, 10.1, 79.2, 82.2]] },
  { code: 'IN', name: 'Indien', kaesten: [[6.5, 35.6, 68.0, 89.5], [6.0, 14.0, 92.2, 94.3]] },
  { code: 'SC', name: 'Seychellen', kaesten: [[-10.4, -3.6, 45.9, 56.5]] },
  { code: 'MU', name: 'Mauritius', kaesten: [[-20.7, -19.9, 57.2, 57.9], [-10.6, -10.2, 56.4, 56.8]] },
  { code: 'MG', name: 'Madagaskar', kaesten: [[-26.0, -11.7, 42.9, 50.7]] },
  { code: 'KM', name: 'Komoren', kaesten: [[-12.6, -11.2, 43.1, 44.7]] },
  { code: 'MZ', name: 'Mosambik', kaesten: [[-27.0, -10.3, 30.1, 41.0]] },
  { code: 'TZ', name: 'Tansania', kaesten: [[-11.9, -0.8, 29.2, 40.6]] },
  { code: 'KE', name: 'Kenia', kaesten: [[-4.8, 5.1, 33.8, 42.1]] },
  { code: 'ZA', name: 'Südafrika', kaesten: [[-35.2, -22.0, 16.2, 33.1]] },

  // --- Südostasien. Borneo teilen sich drei Staaten, deshalb je ein eigener
  // Kasten für Sabah und Sarawak — sonst fiele Sangalaki an Malaysia.
  { code: 'TH', name: 'Thailand', kaesten: [
    [6.0, 10.5, 97.2, 99.4],      // Andamanenküste bis zur Grenze bei Ranong
    [5.4, 20.6, 99.0, 105.8],     // Golfküste und Festland
    [15.0, 20.6, 97.3, 101.5],    // Norden, westlich des Golfkastens
  ] },
  { code: 'MM', name: 'Myanmar', kaesten: [[9.2, 28.6, 92.1, 101.2]] },
  { code: 'MY', name: 'Malaysia', kaesten: [
    [0.9, 6.9, 99.4, 104.7],      // Halbinsel
    [4.0, 7.5, 115.0, 119.7],     // Sabah
    [0.8, 5.1, 109.3, 115.6],     // Sarawak
  ] },
  { code: 'BN', name: 'Brunei', kaesten: [[4.0, 5.2, 114.0, 115.4]] },
  { code: 'SG', name: 'Singapur', kaesten: [[1.15, 1.50, 103.6, 104.1]] },
  { code: 'VN', name: 'Vietnam', kaesten: [[8.2, 23.5, 102.1, 110.0]] },
  { code: 'KH', name: 'Kambodscha', kaesten: [[9.9, 14.8, 102.3, 107.7]] },
  { code: 'PH', name: 'Philippinen', kaesten: [[4.4, 21.3, 116.4, 127.0]] },
  { code: 'TL', name: 'Timor-Leste', kaesten: [[-9.7, -8.1, 124.0, 127.4]] },
  { code: 'ID', name: 'Indonesien', kaesten: [
    [-11.2, 6.3, 94.8, 141.1],
  ] },

  // --- Pazifik
  { code: 'PW', name: 'Palau', kaesten: [[2.7, 8.6, 131.0, 135.0]] },
  { code: 'FM', name: 'Mikronesien', kaesten: [[0.8, 10.2, 137.2, 163.5]] },
  { code: 'MH', name: 'Marshallinseln', kaesten: [[4.0, 15.0, 160.5, 173.0]] },
  { code: 'GU', name: 'Guam', kaesten: [[13.1, 13.8, 144.5, 145.1]] },
  { code: 'PG', name: 'Papua-Neuguinea', kaesten: [[-11.8, -0.7, 140.7, 156.2]] },
  { code: 'SB', name: 'Salomonen', kaesten: [[-12.6, -4.9, 155.0, 167.5]] },
  { code: 'VU', name: 'Vanuatu', kaesten: [[-20.6, -13.0, 166.0, 170.4]] },
  { code: 'NC', name: 'Neukaledonien', kaesten: [[-23.0, -18.9, 163.4, 168.4]] },
  { code: 'FJ', name: 'Fiji', kaesten: [[-21.2, -15.4, 176.3, -178.0]] },
  { code: 'TO', name: 'Tonga', kaesten: [[-22.5, -15.0, -176.5, -173.0]] },
  { code: 'WS', name: 'Samoa', kaesten: [[-14.3, -13.3, -172.9, -171.3]] },
  { code: 'CK', name: 'Cookinseln', kaesten: [[-22.2, -8.5, -166.2, -157.1]] },
  { code: 'PF', name: 'Französisch-Polynesien', kaesten: [[-23.6, -7.4, -154.9, -134.2]] },
  { code: 'AU', name: 'Australien', kaesten: [[-44.2, -9.5, 112.4, 154.4]] },
  { code: 'NZ', name: 'Neuseeland', kaesten: [[-47.7, -33.9, 165.8, 179.3]] },
  { code: 'JP', name: 'Japan', kaesten: [[24.0, 45.7, 122.8, 146.0]] },

  // --- Karibik und Amerika
  { code: 'MX', name: 'Mexiko', kaesten: [[14.3, 32.9, -118.6, -86.5]] },
  { code: 'BZ', name: 'Belize', kaesten: [[15.8, 18.6, -89.3, -87.2]] },
  { code: 'GT', name: 'Guatemala', kaesten: [[13.6, 17.9, -92.4, -88.1]] },
  { code: 'HN', name: 'Honduras', kaesten: [[12.9, 16.6, -89.4, -83.0]] },
  { code: 'NI', name: 'Nicaragua', kaesten: [[10.6, 15.1, -87.8, -82.6]] },
  { code: 'CR', name: 'Costa Rica', kaesten: [[7.9, 11.3, -86.0, -82.5], [5.3, 5.7, -87.2, -86.7]] },
  { code: 'PA', name: 'Panama', kaesten: [[6.9, 9.7, -83.1, -77.1]] },
  { code: 'CO', name: 'Kolumbien', kaesten: [[-4.3, 13.6, -79.1, -66.8]] },
  { code: 'VE', name: 'Venezuela', kaesten: [[0.6, 12.6, -73.4, -59.8]] },
  { code: 'CU', name: 'Kuba', kaesten: [[19.6, 23.4, -85.2, -73.9]] },
  { code: 'BS', name: 'Bahamas', kaesten: [[20.8, 27.4, -79.4, -72.5]] },
  { code: 'JM', name: 'Jamaika', kaesten: [[17.6, 18.6, -78.5, -76.1]] },
  { code: 'DO', name: 'Dominikanische Republik', kaesten: [[17.4, 20.0, -72.1, -68.2]] },
  { code: 'PR', name: 'Puerto Rico', kaesten: [[17.8, 18.6, -67.4, -65.2]] },
  { code: 'KY', name: 'Kaimaninseln', kaesten: [[19.2, 19.8, -81.5, -79.6]] },
  { code: 'AW', name: 'Aruba', kaesten: [[12.3, 12.7, -70.2, -69.7]] },
  { code: 'CW', name: 'Curaçao', kaesten: [[11.9, 12.5, -69.3, -68.6]] },
  { code: 'BQ', name: 'Bonaire', kaesten: [[11.9, 12.4, -68.6, -68.1]] },
  { code: 'EC', name: 'Ecuador', kaesten: [[-5.2, 1.6, -81.3, -75.1], [-1.7, 1.9, -92.3, -88.9]] },
  { code: 'PE', name: 'Peru', kaesten: [[-18.5, -0.0, -81.6, -68.6]] },
  { code: 'BR', name: 'Brasilien', kaesten: [[-34.0, 5.4, -74.1, -34.6]] },
  { code: 'US', name: 'Vereinigte Staaten', kaesten: [
    [24.3, 49.5, -125.2, -66.8],  // Festland
    [18.7, 22.5, -160.4, -154.6], // Hawaii
    [51.0, 71.5, -173.0, -129.0], // Alaska
  ] },
  { code: 'CA', name: 'Kanada', kaesten: [[41.6, 70.0, -141.2, -52.5]] },

  // --- Europa und Atlantik
  { code: 'CH', name: 'Schweiz', kaesten: [[45.80, 47.82, 5.94, 10.52]] },
  { code: 'AT', name: 'Österreich', kaesten: [[46.30, 49.10, 9.50, 17.20]] },
  { code: 'DE', name: 'Deutschland', kaesten: [[47.20, 55.10, 5.80, 15.10]] },
  { code: 'IT', name: 'Italien', kaesten: [
    [36.60, 47.10, 6.60, 18.60],
    [45.40, 46.05, 8.40, 9.60],   // die oberitalienischen Seen, südlich der Grenze
  ] },
  { code: 'FR', name: 'Frankreich', kaesten: [[41.30, 51.15, -5.20, 9.60]] },
  { code: 'ES', name: 'Spanien', kaesten: [
    [35.90, 43.90, -9.40, 3.40],
    [27.50, 29.50, -18.30, -13.30],  // Kanaren
    [38.60, 40.20, 1.10, 4.40],      // Balearen
  ] },
  { code: 'PT', name: 'Portugal', kaesten: [
    [36.90, 42.20, -9.60, -6.15],
    [36.80, 39.80, -31.40, -24.80],  // Azoren
    [32.30, 33.20, -17.40, -16.20],  // Madeira
  ] },
  { code: 'CV', name: 'Kap Verde', kaesten: [[14.7, 17.3, -25.5, -22.6]] },
  { code: 'MA', name: 'Marokko', kaesten: [[27.5, 36.1, -13.4, -0.9]] },
  { code: 'MT', name: 'Malta', kaesten: [[35.75, 36.15, 14.10, 14.65]] },
  { code: 'HR', name: 'Kroatien', kaesten: [
    [42.35, 44.00, 15.50, 18.60],
    [44.00, 45.60, 13.40, 16.20],
    [45.00, 46.56, 15.60, 19.45],
  ] },
  { code: 'GR', name: 'Griechenland', kaesten: [[34.70, 41.80, 19.30, 28.30]] },
  { code: 'TR', name: 'Türkei', kaesten: [[35.70, 42.20, 25.90, 44.90]] },
  { code: 'CY', name: 'Zypern', kaesten: [[34.50, 35.75, 32.20, 34.65]] },
  { code: 'GB', name: 'Vereinigtes Königreich', kaesten: [[49.80, 60.90, -8.30, 1.80]] },
  { code: 'IE', name: 'Irland', kaesten: [[51.40, 55.45, -10.60, -5.95]] },
  { code: 'NL', name: 'Niederlande', kaesten: [[50.70, 53.60, 3.30, 7.25]] },
  { code: 'NO', name: 'Norwegen', kaesten: [[57.90, 71.30, 4.50, 31.20]] },
  { code: 'SE', name: 'Schweden', kaesten: [[55.30, 69.10, 10.90, 24.20]] },
  { code: 'DK', name: 'Dänemark', kaesten: [[54.55, 57.80, 8.00, 12.75]] },
  { code: 'FI', name: 'Finnland', kaesten: [[59.70, 70.10, 20.50, 31.60]] },
  { code: 'IS', name: 'Island', kaesten: [[63.20, 66.60, -24.60, -13.40]] },
];

// ------------------------------------------------------------------ Gewässer
//
// Meere nach den Grenzen der IHO, gerundet auf Kästen; dazu die Seen, in denen
// hierzulande getaucht wird. Ein See ist immer kleiner als das Meer, das ihn
// umgibt, und gewinnt damit von selbst.

export const GEWAESSER = [
  // --- Seen im Alpenraum
  { name: 'Vierwaldstättersee', kaesten: [[46.85, 47.06, 8.28, 8.68]] },
  { name: 'Zürichsee', kaesten: [[47.17, 47.38, 8.53, 8.87]] },
  { name: 'Walensee', kaesten: [[47.10, 47.16, 9.05, 9.32]] },
  { name: 'Zugersee', kaesten: [[47.06, 47.23, 8.42, 8.58]] },
  { name: 'Ägerisee', kaesten: [[47.10, 47.17, 8.59, 8.69]] },
  { name: 'Genfersee', kaesten: [[46.19, 46.53, 6.12, 6.95]] },
  { name: 'Bodensee', kaesten: [[47.42, 47.86, 8.95, 9.82]] },
  { name: 'Thunersee', kaesten: [[46.64, 46.79, 7.60, 7.86]] },
  { name: 'Brienzersee', kaesten: [[46.68, 46.78, 7.87, 8.10]] },
  { name: 'Neuenburgersee', kaesten: [[46.76, 47.06, 6.60, 7.12]] },
  { name: 'Murtensee', kaesten: [[46.87, 47.00, 7.02, 7.16]] },
  { name: 'Bielersee', kaesten: [[47.02, 47.16, 7.02, 7.30]] },
  { name: 'Hallwilersee', kaesten: [[47.24, 47.34, 8.18, 8.26]] },
  { name: 'Sempachersee', kaesten: [[47.11, 47.19, 8.11, 8.25]] },
  { name: 'Sarnersee', kaesten: [[46.84, 46.92, 8.19, 8.28]] },
  { name: 'Lungerersee', kaesten: [[46.76, 46.82, 8.13, 8.21]] },
  { name: 'Klöntalersee', kaesten: [[47.01, 47.06, 8.93, 9.06]] },
  { name: 'Sihlsee', kaesten: [[47.09, 47.20, 8.74, 8.84]] },
  { name: 'Silvaplanersee', kaesten: [[46.43, 46.49, 9.76, 9.85]] },
  { name: 'Caumasee', kaesten: [[46.79, 46.83, 9.26, 9.32]] },
  { name: 'Lago Maggiore', kaesten: [[45.72, 46.20, 8.48, 8.93]] },
  { name: 'Luganersee', kaesten: [[45.86, 46.11, 8.82, 9.12]] },
  { name: 'Comersee', kaesten: [[45.79, 46.21, 9.04, 9.44]] },
  { name: 'Gardasee', kaesten: [[45.41, 45.91, 10.49, 10.93]] },
  { name: 'Attersee', kaesten: [[47.74, 47.98, 13.46, 13.62]] },
  { name: 'Traunsee', kaesten: [[47.77, 47.95, 13.74, 13.86]] },
  { name: 'Wolfgangsee', kaesten: [[47.69, 47.79, 13.34, 13.53]] },
  { name: 'Hallstätter See', kaesten: [[47.52, 47.63, 13.61, 13.71]] },
  { name: 'Grundlsee', kaesten: [[47.60, 47.66, 13.79, 13.95]] },
  { name: 'Achensee', kaesten: [[47.41, 47.57, 11.67, 11.77]] },
  { name: 'Weissensee', kaesten: [[46.67, 46.74, 13.19, 13.43]] },
  { name: 'Starnberger See', kaesten: [[47.82, 48.01, 11.23, 11.37]] },
  { name: 'Walchensee', kaesten: [[47.54, 47.64, 11.27, 11.41]] },
  { name: 'Königssee', kaesten: [[47.49, 47.61, 12.94, 13.04]] },

  // --- Rotes Meer und der arabische Raum
  { name: 'Golf von Aqaba', kaesten: [[29.25, 29.62, 34.85, 35.15]] },
  { name: 'Golf von Suez', kaesten: [[27.55, 30.05, 32.25, 34.15]] },
  { name: 'Rotes Meer', kaesten: [[12.40, 30.10, 32.20, 43.60]] },
  { name: 'Golf von Aden', kaesten: [[10.40, 15.50, 43.00, 52.00]] },
  { name: 'Persischer Golf', kaesten: [[23.80, 30.50, 47.50, 56.60]] },
  { name: 'Golf von Oman', kaesten: [[22.50, 26.70, 56.50, 61.50]] },
  { name: 'Arabisches Meer', kaesten: [[5.00, 25.50, 51.00, 75.00]] },

  // --- Indischer Ozean und Randmeere
  { name: 'Lakkadivensee', kaesten: [[-1.00, 14.00, 71.00, 80.00]] },
  { name: 'Golf von Bengalen', kaesten: [[5.00, 22.50, 80.00, 95.00]] },
  { name: 'Andamanensee', kaesten: [[5.00, 17.00, 92.00, 99.20]] },
  { name: 'Strasse von Malakka', kaesten: [[0.90, 6.50, 96.00, 104.00]] },
  { name: 'Golf von Thailand', kaesten: [[5.50, 13.60, 99.20, 105.50]] },
  { name: 'Mosambik-Kanal', kaesten: [[-26.50, -11.00, 32.00, 49.00]] },

  // --- Indonesien, Philippinen, westlicher Pazifik
  { name: 'Südchinesisches Meer', kaesten: [[-3.00, 23.50, 102.00, 122.00]] },
  { name: 'Sulusee', kaesten: [[4.60, 12.50, 116.50, 123.00]] },
  { name: 'Sulawesisee', kaesten: [[0.00, 7.00, 117.00, 125.50]] },
  { name: 'Javasee', kaesten: [[-7.50, -2.50, 105.00, 117.00]] },
  { name: 'Balisee', kaesten: [[-8.60, -6.90, 114.00, 117.00]] },
  { name: 'Floressee', kaesten: [[-9.20, -6.80, 117.00, 123.50]] },
  { name: 'Savusee', kaesten: [[-10.90, -8.50, 120.00, 125.00]] },
  { name: 'Bandasee', kaesten: [[-8.50, -3.50, 123.00, 134.00]] },
  { name: 'Molukkensee', kaesten: [[-2.00, 4.50, 123.00, 127.50]] },
  { name: 'Halmaherasee', kaesten: [[-3.00, 1.50, 126.80, 131.50]] },
  { name: 'Ceramsee', kaesten: [[-5.20, -0.50, 127.00, 135.00]] },
  { name: 'Arafurasee', kaesten: [[-12.00, -5.00, 130.00, 142.00]] },
  { name: 'Timorsee', kaesten: [[-13.50, -8.00, 123.00, 133.00]] },
  { name: 'Bohol-See', kaesten: [[8.50, 10.30, 122.50, 126.00]] },
  { name: 'Visayassee', kaesten: [[10.20, 12.30, 122.50, 124.60]] },
  { name: 'Philippinensee', kaesten: [[5.00, 30.00, 122.00, 145.00]] },
  { name: 'Ostchinesisches Meer', kaesten: [[24.00, 33.50, 120.00, 131.00]] },
  { name: 'Japanisches Meer', kaesten: [[33.00, 52.00, 127.00, 142.00]] },
  { name: 'Korallenmeer', kaesten: [[-30.00, -6.00, 142.00, 170.00]] },
  { name: 'Tasmansee', kaesten: [[-45.00, -30.00, 148.00, 175.00]] },

  // --- Amerika
  { name: 'Golf von Kalifornien', kaesten: [[22.80, 31.80, -115.00, -107.00]] },
  { name: 'Golf von Mexiko', kaesten: [
    [18.00, 30.50, -98.00, -90.00],
    [21.60, 30.50, -90.00, -84.00],   // nördlich von Cabo Catoche; östlich davon Karibik
  ] },
  { name: 'Floridastrasse', kaesten: [[23.00, 26.50, -83.50, -78.50]] },
  // Die Westgrenze folgt der Landbrücke: Sonst bekäme die Pazifikküste Costa
  // Ricas das Karibische Meer zugeteilt.
  { name: 'Karibisches Meer', kaesten: [
    [15.50, 22.50, -88.50, -59.00],
    [12.50, 15.50, -87.00, -59.00],
    [9.00, 12.50, -84.00, -59.00],
    [7.50, 9.50, -82.50, -59.00],
  ] },

  // --- Europa
  { name: 'Ligurisches Meer', kaesten: [[43.00, 44.50, 7.00, 10.20]] },
  { name: 'Tyrrhenisches Meer', kaesten: [
    [40.50, 44.00, 9.00, 13.00],
    [37.80, 40.80, 11.50, 16.20],
  ] },
  { name: 'Adriatisches Meer', kaesten: [
    [42.50, 45.90, 12.00, 20.00],
    [39.50, 42.50, 15.00, 20.00],   // südlich der Halbinsel, sonst läge Rom an der Adria
  ] },
  { name: 'Ionisches Meer', kaesten: [[34.80, 40.50, 15.00, 21.50]] },
  { name: 'Ägäisches Meer', kaesten: [[35.00, 41.00, 22.50, 27.50]] },
  { name: 'Schwarzes Meer', kaesten: [[40.90, 47.40, 27.40, 42.00]] },
  { name: 'Mittelmeer', kaesten: [[30.00, 45.90, -6.00, 36.50]] },
  { name: 'Nordsee', kaesten: [[51.00, 61.00, -4.00, 9.00]] },
  { name: 'Ostsee', kaesten: [[53.50, 66.00, 9.50, 30.00]] },
  { name: 'Ärmelkanal', kaesten: [[48.80, 51.20, -5.50, 2.00]] },

  // --- Die Ozeane als Auffangnetz. Grösster Kasten, gewinnt also zuletzt.
  { name: 'Nordatlantik', kaesten: [[0.00, 66.00, -82.00, 0.00]] },
  { name: 'Südatlantik', kaesten: [[-60.00, 0.00, -70.00, 20.00]] },
  { name: 'Indischer Ozean', kaesten: [[-60.00, 25.50, 20.00, 120.00]] },
  { name: 'Nordpazifik', kaesten: [[0.00, 66.00, 120.00, -77.00]] },
  { name: 'Südpazifik', kaesten: [[-60.00, 0.00, 130.00, -70.00]] },
  { name: 'Arktischer Ozean', kaesten: [[66.00, 90.00, -180.00, 180.00]] },
  { name: 'Südlicher Ozean', kaesten: [[-90.00, -60.00, -180.00, 180.00]] },
];

/** Das Land zur Koordinate — `{ code, name }` oder `null`. */
export function landAus(lat, lon) {
  if (!brauchbar(lat, lon)) return null;
  const treffer = kleinsterTreffer(LAENDER, lat, lon);
  return treffer ? { code: treffer.code, name: treffer.name } : null;
}

/** Das Gewässer zur Koordinate — Name oder `null`. */
export function gewaesserAus(lat, lon) {
  if (!brauchbar(lat, lon)) return null;
  return kleinsterTreffer(GEWAESSER, lat, lon)?.name ?? null;
}

/**
 * Die Punkte eines Tauchgangs, in der Reihenfolge des Tauchgangs.
 *
 * Der Einstiegspunkt vom Tauchcomputer ist genauer als der Platz: Auf einem
 * Riff von zwei Kilometern Länge liegt der Platzmarker irgendwo, der GPS-Punkt
 * dort, wo abgetaucht wurde. Der Platz kommt nur dazu, wenn es gar kein GPS
 * gibt — sonst läge ein dritter Marker auf demselben Riff.
 */
export function tauchpunkte(tg, platz) {
  const punkte = [];
  if (brauchbar(tg?.entry_lat, tg?.entry_lon)) {
    punkte.push({ art: 'eintauchen', name: 'Eintauchen', lat: tg.entry_lat, lon: tg.entry_lon });
  }
  if (brauchbar(tg?.exit_lat, tg?.exit_lon)) {
    punkte.push({ art: 'auftauchen', name: 'Auftauchen', lat: tg.exit_lat, lon: tg.exit_lon });
  }
  if (punkte.length === 0 && brauchbar(platz?.lat, platz?.lon)) {
    punkte.push({
      art: 'platz', name: platz.name ?? 'Tauchplatz', lat: platz.lat, lon: platz.lon,
    });
  }
  return punkte;
}

/** Die eine Koordinate, aus der Land und Gewässer abgeleitet werden. */
export function ortsangabe(tg, platz) {
  const [erster] = tauchpunkte(tg, platz);
  return erster ? { lat: erster.lat, lon: erster.lon, quelle: erster.art } : null;
}
